import { FiTrash2, FiPercent, FiX, FiShoppingCart } from 'react-icons/fi';

const OrderCart = ({ 
  cart, 
  removeFromCart, 
  updateQuantity, 
  clearCart, 
  generateReceipt,
  orderType,
  tableNumber,
  customerName,
  discount,
  setShowDiscountModal,
  removeDiscount,
  error
}) => {
  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Apply discount
  let discountAmount = 0;
  if (discount) {
    if (discount.type === 'percentage') {
      discountAmount = subtotal * (discount.value / 100);
    } else {
      discountAmount = discount.value;
    }
  }
  
  const taxableAmount = subtotal - discountAmount;
  const tax = taxableAmount * 0.08;
  const total = taxableAmount + tax;

  // Validation messages
  const showOrderTypeError = !orderType && cart.length > 0;
  const showTableNumberError = orderType === 'Dine In' && !tableNumber && cart.length > 0;
  const showCustomerNameError = orderType === 'Take Out' && !customerName && cart.length > 0;

  return (
    <div id="cart-sidebar" className="w-1/3 bg-white p-6 rounded-lg border border-gray-200 overflow-y-auto shadow-sm">
      <div className="mb-6">
        <div className="flex items-center">
          <FiShoppingCart className="mr-2 text-gray-800" size={20} />
          <h2 className="text-lg font-semibold text-gray-800">Order Cart ({cart.reduce((sum, item) => sum + item.quantity, 0)})</h2>
        </div>
        <div className="text-sm text-gray-600 mt-1 ml-6">
          {orderType === 'Dine In' ? (
            <span>Dine In • Table {tableNumber || 'Not specified'}</span>
          ) : orderType === 'Take Out' ? (
            <span>Take Out • {customerName || 'Customer not specified'}</span>
          ) : (
            <span>Select order type</span>
          )}
        </div>
      </div>
      
      {cart.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>Your cart is empty</p>
          <p className="text-sm mt-1">Add items from the menu to get started</p>
        </div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {cart.map(item => (
              <div key={item.cartId} className="bg-gray-50 p-4 rounded border border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-800">{item.name}</h3>
                    <p className="text-sm text-gray-500">${item.price.toFixed(2)} each</p>
                    {item.specialInstructions && (
                      <p className="text-xs text-gray-500 mt-1 italic">
                        Note: {item.specialInstructions}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeFromCart(item.cartId)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                    aria-label="Remove item"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-gray-200 rounded-md bg-white">
                    <button
                      onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                      className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <span className="px-3 py-1 text-gray-800">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                      className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  <span className="font-medium text-lg">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Validation messages */}
          {showOrderTypeError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
              Please select an order type (Dine In or Take Out)
            </div>
          )}
          {showTableNumberError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
              Please enter a table number
            </div>
          )}
          {showCustomerNameError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
              Please enter customer name
            </div>
          )}

          <div className="border-t border-gray-200 pt-4 space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>

            {discount && (
              <div className="flex justify-between">
                <div className="flex items-center">
                  <span className="text-gray-600">Discount:</span>
                  {discount.reason && (
                    <span className="text-xs text-gray-500 ml-1">({discount.reason})</span>
                  )}
                  <button 
                    onClick={removeDiscount}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    <FiX size={16} />
                  </button>
                </div>
                <span className="font-medium text-red-500">
                  -${discountAmount.toFixed(2)}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Tax (8%):</span>
              <span className="font-medium">${tax.toFixed(2)}</span>
            </div>
            
            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              {!discount && (
                <button 
                  onClick={() => setShowDiscountModal(true)}
                  className="w-full flex items-center justify-center bg-gray-100 text-gray-800 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
                >
                  <FiPercent className="mr-2" />
                  Add Discount
                </button>
              )}
              <button 
                onClick={generateReceipt}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
              >
                Generate Receipt & Send to Kitchen
              </button>
              <button 
                onClick={clearCart}
                className="w-full bg-gray-100 text-gray-800 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderCart;